import {
  memo, useState, useEffect, useCallback, useContext, useMemo,
  type FunctionComponent, type MouseEventHandler
} from 'react';
import isNil from 'lodash/isNil';
// Material UI imports
import Dialog from '@mui/material/Dialog';
// Skillmore UI Components
import CloseIconButton from '@empathco/ui-components/src/elements/CloseIconButton';
// local imports
import { SKILL_LEVEL_MIN, Skill, SkillLevel } from '../models/skill';
import useCustomerSettings from '../config/customer';
import { getSkillCurrentLevel, getSkillCurrentLevelOnly, hasCurrentLevel } from '../helpers/models';
import { GlobalContext } from '../context/global';
import { DataContext } from '../context';
import useSkillLevelConfirmGate from '../hooks/useSkillLevelConfirmGate';
import EditSkillLevel from '../v3/EditSkillLevel';

type SkillLevelDialogProps = {
  readonly skill: Skill;
  readonly defaultLevel?: SkillLevel;
  readonly isOpen?: boolean;
  readonly onUpdate: (level: SkillLevel, mentoring: boolean, targeting: boolean) => void;
  readonly onCancel: MouseEventHandler<HTMLButtonElement>;
  readonly onExited: (node: HTMLElement) => void;
  readonly disabled?: boolean | null;
  readonly pending?: boolean | null;
  readonly plain?: boolean;
  readonly readOnly?: boolean;
  readonly editable?: boolean; // `readOnly` content, but the level can still be saved (manager editing an employee)
  readonly confirmLabel?: string | null;
  readonly removeLabel?: string | null; // replaces `confirmLabel` when the selected level is 0 (`editable` only)
  readonly employeeName?: string | null; // employee being edited: shown as a caption in `editable` mode only
  readonly manualOnly?: boolean;
  readonly depersonalized?: boolean;
  // for Storybook only
  readonly testLevel?: SkillLevel;
  readonly testConfirmed?: boolean;
}

const SkillLevelDialog: FunctionComponent<SkillLevelDialogProps> = ({
  skill,
  defaultLevel,
  isOpen = false,
  onUpdate,
  onCancel,
  onExited,
  disabled = false,
  pending = false,
  plain = false,
  readOnly = false,
  editable = false,
  confirmLabel,
  removeLabel,
  employeeName,
  manualOnly = false,
  depersonalized = false,
  testLevel,
  testConfirmed
  // eslint-disable-next-line complexity
}) => {
  const { HAS_MENTORING } = useCustomerSettings();
  const { user: { data: user } } = useContext(GlobalContext);
  const { requirePreferences } = useContext(DataContext);
  const levelConfirmGate = useSkillLevelConfirmGate();

  const { is_mentoring, is_target } = skill || {};
  const currentLevel = getSkillCurrentLevel(skill);
  const currentOnlyLevel = getSkillCurrentLevelOnly(skill);

  const isAdminOnly = Boolean(user?.is_admin_only);
  const needsPreferences = !isAdminOnly && !depersonalized;
  const canMentor = HAS_MENTORING && !isAdminOnly && !manualOnly && !depersonalized;
  const canTarget = !isAdminOnly && !manualOnly && !depersonalized;
  const isMentoring = canMentor && Boolean(is_mentoring);
  const isTargeting = canTarget && Boolean(is_target);

  useEffect(() => {
    if (needsPreferences) requirePreferences?.();
  }, [needsPreferences, requirePreferences]);

  const [level, setLevel] = useState<SkillLevel>(defaultLevel || SKILL_LEVEL_MIN);
  const [mentoring, setMentoring] = useState(false);
  const [isTarget, setIsTarget] = useState(false);

  const handleUpdate = useCallback((_skl: Skill, lvl: SkillLevel, mentor: boolean, target?: boolean) => {
    setLevel(lvl);
    setMentoring(mentor);
    if (!isNil(target)) setIsTarget(target);
  }, []);

  const handleSave = useCallback(() => onUpdate(level, mentoring, isTarget), [level, mentoring, isTarget, onUpdate]);

  const transitionProps = useMemo(() => ({ onExited }), [onExited]);

  // `plain` is deliberately not excluded: the profile builder is an own-skill flow
  const requireLevelConfirm = levelConfirmGate && !editable && !readOnly && !manualOnly && !depersonalized;

  // in `editable` mode only the level can change: mentoring / targeting reflect the current user, not the edited
  // employee. So the manager's confirm button stays disabled until they move the selection off the level the dialog
  // opened with — `EditSkillLevel` derives the same value and pushes it up through `onUpdate` on mount. Without this
  // the shared expression below leaves the button live on open whenever a newer inference supersedes a stored level
  // (`currentOnlyLevel` and `hasCurrentLevel` both go falsy there), and one click would save a level nobody picked.
  // In editable mode the button is live only when saving would change something. Two ways it would
  // not: the selection already matches the level stored on the profile, or the dialog merely opened
  // on the skill's own current level and nothing has been touched — without that second clause a
  // stored level superseded by a newer inference arms the button on open, and one click saves a
  // level the manager never picked. An explicit `defaultLevel` is the caller preselecting on
  // purpose (a removal opens at 0), so it does not have to be moved off before saving.
  const confirmDisabled = editable
    ? level === currentOnlyLevel || (isNil(defaultLevel) && level === currentLevel)
    : currentLevel === level && currentOnlyLevel === level &&
      (isNil(defaultLevel) || defaultLevel === level) &&
      (!canMentor || isMentoring === mentoring) && (!canTarget || isTargeting === isTarget) &&
      hasCurrentLevel(skill);

  return (
    <Dialog
        disableEnforceFocus
        maxWidth="lg"
        fullWidth
        scroll="body"
        open={isOpen}
        onClose={pending ? undefined : onCancel}
        TransitionProps={transitionProps}
    >
      <CloseIconButton onClick={onCancel} disabled={pending ? true : undefined}/>
      {skill ? (
        <EditSkillLevel
            readOnly={readOnly}
            editable={editable}
            confirmLabel={confirmLabel}
            removeLabel={removeLabel}
            employeeName={employeeName}
            plain={plain || manualOnly}
            noTargeting={!canTarget}
            noMentoring={!canMentor}
            depersonalized={depersonalized}
            skill={skill}
            defaultLevel={defaultLevel}
            onUpdate={handleUpdate}
            onBack={depersonalized ? onCancel : undefined}
            onConfirm={handleSave}
            disabled={disabled || pending}
            confirmPending={pending}
            confirmDisabled={confirmDisabled ? true : undefined}
            requireLevelConfirm={requireLevelConfirm}
            testLevel={testLevel}
            testConfirmed={testConfirmed}
        />
      ) : undefined}
    </Dialog>
  );
};

export default memo(SkillLevelDialog);
